import { REWARDS_HUB_MOCK } from '../rewards-hub/mocks';
import type { RewardBonusDto } from '../rewards-hub/types';
import { toBoardDto, toDirectionDto, toMetricDto, toPeriodDto } from './mappers';
import type {
  LeaderboardMetric,
  LeaderboardPeriod,
  LeaderboardSegment,
  LeaderboardSortDirection,
} from './models';
import type { LeaderboardDto, LeaderboardMetricDto, LeaderboardRowDto } from './types';

/**
 * Local fixtures for `NEXT_PUBLIC_REWARDS_HUB_MOCK=1` — the same switch the
 * rest of the Rewards Hub answers to, so one flag brings up the whole section
 * without a gamification backend. The figures are the ones Figma 13449:466421
 * prints, extended to a full top 20.
 *
 * The mock answers in the wire shape, so the mapper — dedup of `me`, the
 * echoed request, the leaders — runs exactly as it does against the service.
 */
export { REWARDS_HUB_MOCK as REWARDS_LEADERBOARD_MOCK };

/**
 * `[nickname, rank, volume, trades, best deal, ROE %, tasks]` — one row per
 * line. Best deal and ROE are `null` for a player with no closed deal (29.09).
 */
type SeedTuple = [
  string | null,
  number | null,
  number,
  number,
  number | null,
  number | null,
  number,
];

/**
 * Each category is led by a different player, so the five winner cards show
 * five faces — a board where one account tops everything says nothing about
 * the cards. A nameless and a rankless player are in there on purpose.
 */
const SEEDS: SeedTuple[] = [
  ['Market_Master', 6, 2_480_300, 9_640, 9_120.4, 402, 188],
  ['AlphaTrader', 5, 1_982_100, 12_201, 8_640.15, 388, 164],
  ['MasterCrypto', 6, 1_840_600, 8_120, 12_398.2, 430, 151],
  ['Traderina', 5, 1_702_900, 7_980, 7_860.05, 486, 142],
  ['TechGuru007', 4, 1_650_000, 9_200, 8_900.0, 420, 323],
  ['Username3029', 3, 1_617_400, 7_430, 7_502.9, 396, 131],
  ['GamerKing99', 4, 1_205_750, 6_890, 5_230.25, 370, 115],
  [null, 2, 1_125_900, 4_780, 4_400.5, 315, 110],
  ['EcoWarrior88', 3, 980_400, 5_530, 3_820.1, 250, 98],
  ['ArtisanCrafts', 4, 750_400, 3_600, 2_150.75, 200, 75],
  ['CryptoNinja', 4, 690_200, 4_310, 1_980.4, 185, 68],
  ['SolanaQueen', 3, 655_800, 4_050, 1_840.0, 172, 64],
  ['BullRunner', 3, 612_450, 3_870, 1_705.6, 160, 61],
  ['MoonTrader88', 4, 580_900, 3_640, 1_560.25, 148, 57],
  ['DeFiDaisy', 2, 534_300, 3_420, 1_420.8, 138, 53],
  ['ChartWizard', 3, 496_750, 3_180, 1_295.1, 124, 49],
  ['HodlHannah', 2, 452_100, 2_940, 1_150.35, 112, 45],
  ['AlphaSeeker', 3, 418_600, 2_710, 1_020.8, 101, 42],
  ['QuietWhale', 2, 385_250, 2_480, 905.45, 92, 38],
  ['SteadyEddie', 2, 341_900, 2_260, 812.3, 84, 34],
  ['PaperHandsPete', 1, 298_400, 1_980, -240.15, -18, 27],
  ['StillHolding', 2, 210_300, 1_460, null, null, 22],
  ['FirstTimer', null, 152_050, 1_120, null, null, 0],
];

/** The signed-in player: far down the board, so the page has to pin their row. */
const ME_SEED: SeedTuple = ['Princess123', 6, 125_900, 4_780, 4_400.5, 315, 110];
const ME_ID = 'mock-me';
const ME_POSITION = 1_767;

/** Players on the board — far more than the 20 the table shows. */
const BOARD_SIZE = 2_140;

function toRow(
  [name, rank, vol, tr, bd, roe, tk]: SeedTuple,
  position: number,
  id: string,
): LeaderboardRowDto {
  return {
    p: position,
    u: id,
    n: name,
    vol,
    tr,
    bd,
    roe,
    tk,
    rk: rank,
    av: null,
    ach: rank === null ? 0 : tk % 40,
    md: rank === null ? 0 : rank + 1,
    cp: rank === null ? 0 : Math.max(0, rank - 2),
    dm: rank === null ? 0 : Math.max(0, rank - 4),
  };
}

const VALUE_OF: Record<LeaderboardMetric, (seed: SeedTuple) => number | null> = {
  volume: (seed) => seed[2],
  trades: (seed) => seed[3],
  bestDeal: (seed) => seed[4],
  roe: (seed) => seed[5],
  tasks: (seed) => seed[6],
};

/** What the service orders equal figures by (02.11), before the player id. */
const TIE_BREAK: Partial<Record<LeaderboardMetric, LeaderboardMetric>> = {
  trades: 'volume',
  tasks: 'volume',
  volume: 'trades',
};

const figureOf = (seed: SeedTuple, metric: LeaderboardMetric) =>
  VALUE_OF[metric](seed) ?? Number.NEGATIVE_INFINITY;

/**
 * Best first, a player with no figure after everyone, equal figures by the
 * second column — as the service sorts.
 */
function byMetric(metric: LeaderboardMetric) {
  const tieBreak = TIE_BREAK[metric];
  return (a: SeedTuple, b: SeedTuple) => {
    const byFigure = figureOf(b, metric) - figureOf(a, metric);
    if (byFigure || !tieBreak) return byFigure || 0;
    return figureOf(b, tieBreak) - figureOf(a, tieBreak);
  };
}

/** A filler row far down the board: small, decreasing figures. */
function fillerSeed(index: number): SeedTuple {
  const scale = Math.max(1, BOARD_SIZE - index);
  return [`Player${10_000 + index}`, (index % 5) + 1, scale * 60, scale, scale / 4, 20, index % 9];
}

const prizeOf = (amt: number): RewardBonusDto => ({
  bt: 'FIXED_BONUS',
  amt,
  un: 'USD',
  max: null,
  av: true,
});

const TRADING_PRIZES: LeaderboardDto['pz'] = {
  VOLUME: prizeOf(100),
  TRADES: prizeOf(100),
  BEST_DEAL: prizeOf(100),
  BEST_ROE: prizeOf(100),
};

/** Like the service (26.10): the four trading prizes, and Tasks on the year alone. */
const MOCK_PRIZES: Partial<Record<LeaderboardPeriod, LeaderboardDto['pz']>> = {
  month: TRADING_PRIZES,
  year: { ...TRADING_PRIZES, TASKS: prizeOf(500) },
};

interface MockLeaderboardParams {
  segment: LeaderboardSegment;
  metric: LeaderboardMetric;
  period?: LeaderboardPeriod;
  page: number;
  direction?: LeaderboardSortDirection;
}

/**
 * How a shorter period reshapes the board (07.10): a share of the volume and
 * trades, fewer players (no deals in the period, no place on its board), on
 * the month some without a deal closed in it (Best Deal / ROE "—"). Tasks are
 * all-time and stay. The player has no deals this month: no `me`, the "—" row.
 */
const PERIOD_SHAPE: Record<
  LeaderboardPeriod,
  { share: number; boardShare: number; noDealEvery: number | null; me: boolean }
> = {
  all: { share: 1, boardShare: 1, noDealEvery: null, me: true },
  year: { share: 0.55, boardShare: 0.6, noDealEvery: null, me: true },
  month: { share: 0.07, boardShare: 0.15, noDealEvery: 3, me: false },
  day: { share: 0.004, boardShare: 0.05, noDealEvery: 2, me: false },
};

function seedForPeriod(seed: SeedTuple, index: number, period: LeaderboardPeriod): SeedTuple {
  const { share, noDealEvery } = PERIOD_SHAPE[period];
  if (share === 1) return seed;
  const [name, rank, vol, tr, bd, roe, tk] = seed;
  // Each player's share of their own figures differs a little, so the order moves too.
  const own = share * (0.7 + ((index * 37) % 60) / 100);
  const noDeal = noDealEvery !== null && index % noDealEvery === 1;
  return [
    name,
    rank,
    Math.round(vol * own),
    Math.max(1, Math.round(tr * own)),
    noDeal ? null : bd,
    noDeal ? null : roe,
    tk,
  ];
}

/**
 * Nobody on Prediction has a profitable deal of $100+ closed today, as on the
 * service's Prediction / This month (02.11): Best Deal and ROE are "—" throughout.
 */
const isDealless = (segment: LeaderboardSegment, period: LeaderboardPeriod) =>
  segment === 'prediction' && period === 'day';

const withoutDeal = ([name, rank, vol, tr, , , tk]: SeedTuple): SeedTuple => [
  name,
  rank,
  vol,
  tr,
  null,
  null,
  tk,
];

/** The ranking the service pages through, and the rows a page holds (28.09). */
const TOP = 200;
const PAGE_SIZE = 20;
/** On Crypto the player sits inside the top 200, so its pages show the in-place row too. */
const ME_POSITION_CRYPTO = 57;

/**
 * The places of every page, in reading order, as the service cuts them:
 * twenty a page, the player among them on their own page and the 20th row on
 * the others — best first only on the pages before their own, worst first on
 * every one (02.11), outside the top on every one either way. Nobody is pushed
 * off: every pinned row shifts the rest down one.
 */
function mockPages(order: number[], mePlace: number | null, pinsAfterOwnPage: boolean): number[][] {
  const pages: number[][] = [];
  const others = order.filter((place) => place !== mePlace);
  // -1 with no standing, or a place outside the top.
  const meSlot = mePlace === null ? -1 : order.indexOf(mePlace);
  let taken = 0;
  let pastOwnPage = false;
  while (taken < others.length) {
    const isOwnPage = !pastOwnPage && meSlot >= taken && meSlot < taken + PAGE_SIZE;
    if (isOwnPage) {
      pages.push(order.slice(taken, taken + PAGE_SIZE));
      taken += PAGE_SIZE - 1;
      pastOwnPage = true;
    } else if (mePlace !== null && (meSlot < 0 || !pastOwnPage || pinsAfterOwnPage)) {
      pages.push([...others.slice(taken, taken + PAGE_SIZE - 1), mePlace]);
      taken += PAGE_SIZE - 1;
    } else {
      pages.push(others.slice(taken, taken + PAGE_SIZE));
      taken += PAGE_SIZE;
    }
  }
  return pages;
}

interface Standing {
  place: number;
  seed: SeedTuple;
  id: string;
}

/**
 * Worst first is the top read from its far end — except that a player with no
 * figure (no Best Deal / ROE) stays last (02.11): no deal is not the smallest.
 */
function readingOrder(
  standings: Standing[],
  metric: LeaderboardMetric,
  direction: LeaderboardSortDirection,
): Standing[] {
  if (direction === 'desc') return standings;
  const ranked = standings.filter(({ seed }) => VALUE_OF[metric](seed) !== null);
  const unranked = standings.filter(({ seed }) => VALUE_OF[metric](seed) === null);
  return [...ranked.reverse(), ...unranked];
}

/**
 * The server owns the ordering, so the mock reproduces it rather than letting
 * the page sort — the places the rows print have to survive a sort change.
 * Like the service: the top 200 in pages of twenty, the player pinned as the
 * 20th row (below 200 on All: on every page; 57th on Crypto). Prediction
 * answers without `me` — the player never traded there. Worst first keeps the
 * places, counting down from the far end.
 */
export function mockLeaderboard({
  segment,
  metric,
  period = 'all',
  page,
  direction = 'desc',
}: MockLeaderboardParams): LeaderboardDto {
  const shape = PERIOD_SHAPE[period];
  const boardSize = Math.round(BOARD_SIZE * shape.boardShare);
  const total = Math.min(TOP, segment === 'crypto' ? Math.round(boardSize / 3) : boardSize);

  const mePlace =
    segment === 'prediction' || !shape.me
      ? null
      : segment === 'crypto'
        ? ME_POSITION_CRYPTO
        : Math.round(ME_POSITION * shape.boardShare);
  const me = mePlace === null ? null : toRow(ME_SEED, mePlace, ME_ID);

  // Everyone in the top but the player, ranked under the column.
  const head = segment === 'crypto' ? SEEDS.filter((_, i) => i % 2 === 0) : SEEDS;
  const dealless = isDealless(segment, period);
  const others = Array.from({ length: total }, (_, i) => i + 1).filter(
    (place) => place !== mePlace,
  );
  const players = others.map((_, i) => {
    const seed = seedForPeriod(head[i] ?? fillerSeed(i), i, period);
    return { seed: dealless ? withoutDeal(seed) : seed, id: `mock-${i}` };
  });
  const compare = byMetric(metric);
  const standings: Standing[] = [...players]
    .sort((a, b) => compare(a.seed, b.seed))
    .map((player, i) => ({ ...player, place: others[i] }));
  // The player's own place is theirs: the others step past it.
  if (mePlace !== null && mePlace <= total) {
    standings.splice(mePlace - 1, 0, { place: mePlace, seed: ME_SEED, id: ME_ID });
  }

  const rowByPlace = new Map(
    standings.map(({ seed, place, id }) => [place, toRow(seed, place, id)]),
  );
  if (me) rowByPlace.set(me.p, me);
  const order = readingOrder(standings, metric, direction).map(({ place }) => place);
  const pages = mockPages(order, mePlace, direction === 'asc');
  const rows = (pages[page - 1] ?? []).flatMap((place) => rowByPlace.get(place) ?? []);

  const ld: LeaderboardDto['ld'] = {};
  for (const leader of Object.keys(VALUE_OF) as LeaderboardMetric[]) {
    const compare = byMetric(leader);
    const best = [...players].sort((a, b) => compare(a.seed, b.seed))[0];
    const key: LeaderboardMetricDto = toMetricDto(leader);
    // Nobody with a closed deal: no Best Deal / Best ROE card (29.09).
    ld[key] = best && VALUE_OF[leader](best.seed) !== null ? toRow(best.seed, 1, best.id) : null;
  }

  return {
    b: toBoardDto(segment),
    m: toMetricDto(metric),
    pr: toPeriodDto(period),
    o: toDirectionDto(direction),
    pg: page,
    pgs: pages.length,
    rows,
    me,
    ld,
    pz: MOCK_PRIZES[period],
  };
}
